<template>
  <!--LAYBY LANDING PAGE: Story CD-19 -->
  <Card
    class="w-full max-w-[510px] min-h-[545px] overflow-hidden shadow-md rounded-xl h-full"
    :class="`${cardClasses} ${isMobileOrIsTable ? 'min-h-[343px]' : ''}`"
  >
    <CardContent class="p-0 flex flex-col h-full" :class="contentClasses">
      <div class="p-6 flex-1">
        <div
          class="mb-4 items-start"
          :class="{
            'flex justify-between items-center': isMobileOrIsTable || (isSuspended && !hasCancellationRequest),
          }"
        >
          <div>
            <p class="text-[16px] font-semibold" :class="titleClasses">{{ currentOrder?.merchantName }}</p>
            <p class="text-[14px] font-small" :class="titleClasses" v-if="isPayAtEnabled">
              Pay@ Ref: <span class="font-medium">{{ paymentPlan?.payAtReferenceNumber }}</span>
            </p>
            <p class="text-[14px] font-small" :class="titleClasses">{{ currentOrder?.reference }}</p>
          </div>
          <div
            v-if="isSuspended && !hasCancellationRequest"
            class="bg-warning-50 text-warning-700 border-warning-200 border-[1px] h-[28px] rounded-[9999px] flex justify-center items-center px-3 text-[12px] font-medium"
          >
            Suspended
          </div>
        </div>

        <div class="h-[40px]">
          <div class="flex justify-between mb-4" v-if="!isCompleted">
            <p class="text-[14px] text-grey-light-600">Target</p>
            <p class="text-[24px] font-semibold text-brand-700">
              {{ displayCurrency(orderTotal) }}
            </p>
          </div>
        </div>

        <div class="relative mb-3">
          <img
            :src="currentOrder?.imageUrl || DefaultLayupLogo"
            alt="Product"
            class="w-full rounded-[16px] object-contain"
            :class="isMobileOrIsTable ? 'h-[150px]' : 'h-[229px]'"
          />
        </div>

        <div v-if="!isCompleted">
          <div class="mb-3 flex items-end justify-between">
            <div>
              <p class="text-[14px] text-grey-light-600">Contributed</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(paidAmountInCents) }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-[14px] text-grey-light-600">Outstanding</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(orderBalance) }}
              </p>
            </div>
          </div>

          <div class="mb-3">
            <div class="flex gap-0 mb-0">
              <div class="relative h-2 w-full overflow-hidden rounded-full bg-grey-light-200">
                <div class="h-full transition-all" :class="progressBarClasses" :style="{ width: `${progress}%` }" />
              </div>
            </div>

            <div class="mt-2 flex items-center justify-between text-xs">
              <span class="text-[14px] text-grey-light-600"> {{ remainingMonths }}</span>
              <span v-if="!isCancelled && !isSuspended" class="font-medium text-[14px] text-grey-light-700"
                >{{ progress }}%</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="mt-auto">
        <hr :class="isCompleted ? 'border-brand-100' : ''" />
        <div class="flex gap-2 px-6 py-4 h-[73px]" :class="isCompleted ? 'justify-center' : 'justify-between'">
          <Button
            v-if="!isCompleted"
            variant="outline"
            size="sm"
            class="w-[66px] h-[40px] font-semibold"
            @click="navigateToDetails"
          >
            View
          </Button>

          <div
            v-if="isCompleted"
            size="sm"
            class="flex items-center justify-center gap-2 border-[1px] h-[40px] rounded-lg w-[180px]"
            :class="
              props.isPaymentPlan
                ? 'border-blue-200 text-blue-400 bg-blue-50'
                : 'border-brand-300 text-brand-300 bg-brand-25 hover:bg-brand-25'
            "
          >
            <Check class="h-3 w-3" />
            Order Completed
          </div>

          <div
            v-else-if="isCancelled"
            size="sm"
            class="bg-error-50 text-error-700 hover:bg-error-50 min-w-[100px] border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Cancelled
          </div>

          <div
            v-else-if="isCancelling"
            size="sm"
            class="bg-error-50 text-error-700 hover:bg-error-50 border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-3 whitespace-nowrap text-xs sm:text-sm"
          >
            Cancellation Requested
          </div>

          <div
            v-else-if="isSuspended && hasCancellationRequest"
            size="sm"
            class="bg-warning-50 text-warning-700 hover:bg-warning-50 min-w-[100px] border-warning-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Suspended
          </div>

          <Button
            v-else-if="isSuspended"
            size="sm"
            class="bg-brand-600 hover:bg-brand-700 w-[132px] h-[40px] font-semibold"
            @click="showPaymentModal = true"
          >
            Make Payment
          </Button>

          <Button
            v-else
            size="sm"
            class="bg-brand-600 hover:bg-brand-700 w-[132px] h-[40px] font-semibold"
            @click="showPaymentModal = true"
          >
            Make Payment
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>

  <Dialog :open="showPaymentModal" @update:open="onDialogUpdate" class="p-6">
    <DialogContent :class="dialogWidthClass" preventClose>
      <ContributeModal
        v-if="currentOrder"
        :order="currentOrder"
        :upcoming="upcoming"
        @close="onContributeClose"
        @completed="onPaymentCompleted"
        @step-change="contributeStep = $event"
      />
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import DefaultLayupLogo from '@/assets/logo.svg';
import ContributeModal from '@/components/shopper/contribute/ContributeModal.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { remainingTimeLabel, upcomingPayments } from '@/utils/paymentPlan.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { Check } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

interface Props {
  order?: ModelOrderResponse;
  isPaymentPlan?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{ 'payment-completed': [] }>();
const router = useRouter();
const showPaymentModal = ref(false);
const contributeStep = ref<string>('');

const localOrder = ref<ModelOrderResponse | undefined>(undefined);
const currentOrder = computed<ModelOrderResponse | undefined>(() => localOrder.value ?? props.order);

const dialogWidthClass = computed(() => [
  contributeStep.value === 'payment'
    ? // Full-screen on mobile for the payment step; revert to a centered card on sm+.
      'inset-0 left-0 top-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none max-h-none rounded-none p-4 overflow-y-auto sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:h-auto sm:w-full sm:max-h-[90vh] sm:rounded-xl sm:p-6 transition-all duration-300'
    : 'w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto transition-all duration-300 rounded-xl p-3 sm:p-6',
  contributeStep.value === 'debi-check'
    ? 'sm:max-w-[640px]'
    : contributeStep.value === 'select-method' || contributeStep.value === 'status'
      ? 'sm:max-w-[560px]'
      : 'sm:max-w-[420px]',
]);
const { isMobile, isTablet } = useBreakpoint();

const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);
const isCompleted = computed(() => currentOrder.value?.state === 'COMPLETED');
const isCancelled = computed(() => currentOrder.value?.state === 'CANCELLED');
const isCancelling = computed(() => currentOrder.value?.state === 'CANCELLING');
const isSuspended = computed(() => currentOrder.value?.state === 'SUSPENDED');
const hasCancellationRequest = computed(() => currentOrder.value?.orderCancellationRequest === 'ACCEPTED');

const orderBalance = computed(() => currentOrder.value?.balance ?? 0);
const orderTotal = computed(() => currentOrder.value?.amountDue ?? 0);

const cardClasses = computed(() => {
  if (isCompleted.value && props.isPaymentPlan) {
    return 'border-[1px] border-blue-200 bg-blue-50';
  }

  if (isCompleted.value) {
    return 'border-[1px] border-brand-300 bg-brand-25';
  }

  if (isCancelled.value || (isSuspended.value && hasCancellationRequest.value)) {
    return 'border-[1px] border-grey-light-200 shadow-grey-light-200 opacity-50';
  }

  return 'border-[1px] border-grey-light-200 shadow-grey-light-200';
});

const contentClasses = computed(() => {
  return isCancelled.value || (isSuspended.value && hasCancellationRequest.value) ? 'opacity-80' : '';
});

const titleClasses = computed(() => {
  if (isCompleted.value && props.isPaymentPlan) {
    return 'text-blue-700';
  }

  if (isCompleted.value) {
    return 'text-brand-500';
  }

  return 'text-grey-light-900';
});

const progressBarClasses = computed(() => {
  if (isCompleted.value) {
    return 'bg-brand-600';
  }

  if (isCancelled.value || isSuspended.value) {
    return 'bg-grey-light-300';
  }

  return 'bg-brand-600';
});

const progress = computed(() => {
  if (!orderTotal.value) return 0;

  return Math.round(((orderTotal.value - orderBalance.value) / orderTotal.value) * 100);
});

const paidAmountInCents = computed(() => {
  if (orderTotal.value === 0) return 0;

  return orderTotal.value - orderBalance.value;
});

const upcoming = computed(() => {
  const plan = currentOrder.value?.plans?.[0];
  return plan ? upcomingPayments(plan as any) : [];
});

const paymentPlan = computed(() => currentOrder.value?.plans?.[0]);

const remainingMonths = computed(() => remainingTimeLabel(currentOrder.value?.plans?.[0], isCompleted.value));

const isPayAtEnabled = computed(() => currentOrder.value?.paymentMethods?.payAt!);

const onContributeClose = () => {
  showPaymentModal.value = false;
  if (contributeStep.value === 'status') {
    onPaymentCompleted();
  }
};

const onDialogUpdate = (open: boolean) => {
  if (!open) {
    onContributeClose();
  }
};

const onPaymentCompleted = async () => {
  showPaymentModal.value = false;
  const id = props.order?._id;
  if (id) {
    try {
      localOrder.value = await getOrderByIdWithPlansAndPayments(id);
      addMetric('contribute_completed', { channel: 'shopper', orderId: id, orderType: 'layby' });
    } catch (error) {
      createException(error);
    }
  }
  emit('payment-completed');
};

const navigateToDetails = () => {
  router.push({ name: 'shopperLaybyDetails', params: { id: currentOrder.value?._id } });
};
</script>
